/**
 * Sanitization Utilities for Frontend
 * =====================================
 * 
 * Provides XSS protection using DOMPurify for any user-generated
 * or LLM-generated content that will be rendered as HTML.
 * 
 * Usage:
 *   import { sanitizeHtml, sanitizeLlmResponse } from '@/utils/sanitize'
 *   
 *   // In template with v-html
 *   <div v-html="sanitizeHtml(untrustedContent)"></div>
 *   
 *   // Or use the formatMessage helper
 *   <div v-html="formatMessage(message)"></div>
 */

import DOMPurify from 'dompurify'

// Configure DOMPurify with safe defaults
const purifyConfig = {
  // Allowed HTML tags (safe subset for content display)
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'b', 'em', 'i', 'u',
    'ul', 'ol', 'li',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'blockquote', 'code', 'pre',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'a', 'span', 'div'
  ],
  
  // Allowed attributes on tags
  ALLOWED_ATTR: [
    'href', 'title', 'target', 'rel',
    'class', 'id',
    'colspan', 'rowspan'
  ],
  
  // Only allow safe URL protocols
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  
  // Force links to open in new tab with safe attributes
  ADD_ATTR: ['target', 'rel'],
  
  // Prevent data exfiltration
  FORBID_TAGS: ['script', 'style', 'iframe', 'form', 'input', 'textarea', 'button'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur']
}

// Hook to add rel="noopener noreferrer" to all links
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank')
    node.setAttribute('rel', 'noopener noreferrer')
  }
})

/**
 * Sanitize HTML content to prevent XSS attacks.
 * 
 * @param {string} content - Raw HTML content to sanitize
 * @returns {string} Sanitized HTML safe for v-html
 */
export function sanitizeHtml(content) {
  if (!content) return ''
  return DOMPurify.sanitize(content, purifyConfig)
}

/**
 * Sanitize LLM-generated response for safe display.
 * Converts markdown-like formatting to safe HTML.
 * 
 * @param {string} content - Raw LLM response
 * @returns {string} Sanitized HTML safe for v-html
 */
export function sanitizeLlmResponse(content) {
  if (!content) return ''
  
  // Convert markdown-like formatting to HTML
  let html = content
    // Bold: **text** or __text__
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.*?)__/g, '<strong>$1</strong>')
    // Italic: *text* or _text_
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/_([^_]+)_/g, '<em>$1</em>')
    // Code: `text`
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    // Line breaks
    .replace(/\n/g, '<br>')
  
  // Sanitize the result
  return DOMPurify.sanitize(html, purifyConfig)
}

/**
 * Format a chat message for display.
 * Alias for sanitizeLlmResponse with consistent naming.
 * 
 * @param {string} content - Message content
 * @returns {string} Sanitized HTML safe for v-html
 */
export function formatMessage(content) {
  return sanitizeLlmResponse(content)
}

/**
 * Strip all HTML tags, returning plain text.
 * Useful for displaying content in contexts where HTML is not allowed.
 * 
 * @param {string} content - HTML content
 * @returns {string} Plain text with all HTML removed
 */
export function stripHtml(content) {
  if (!content) return ''
  return DOMPurify.sanitize(content, { ALLOWED_TAGS: [] })
}

/**
 * Check if content contains potentially dangerous HTML.
 * 
 * @param {string} content - Content to check
 * @returns {boolean} True if content was modified by sanitization
 */
export function containsDangerousHtml(content) {
  if (!content) return false
  const sanitized = sanitizeHtml(content)
  return sanitized !== content
}

export default {
  sanitizeHtml,
  sanitizeLlmResponse,
  formatMessage,
  stripHtml,
  containsDangerousHtml
}

